import { useEffect, useState } from "react";
import { CheckCircle2, MessageCircle, X } from "lucide-react";
import { api, formatDate } from "@/lib/api";
import { useSettings } from "@/hooks/useSettings";
import { eventMessage, waLink } from "@/lib/whatsapp";

export const WhatsAppRemindersModal = ({ eventId, onClose }) => {
  const settings = useSettings();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [onlyPending, setOnlyPending] = useState(true);
  const load = () => api(`/admin/events/${eventId}/registrants`).then(setData).catch(e => setErr(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

  const markSent = async reg => {
    try { await api("/admin/whatsapp/mark", { method: "POST", body: JSON.stringify({ kind: "registration", id: reg.id }) }); load(); } catch (e) { setErr(e.message); }
  };
  const regs = (data?.registrants || []).filter(r => !onlyPending || !r.whatsapp_sent_at);
  const sent = (data?.registrants || []).filter(r => r.whatsapp_sent_at).length;
  const when = data?.days_until === 0 ? "today" : data?.days_until === 1 ? "tomorrow" : data?.event?.date ? `on ${formatDate(data.event.date)}` : "";

  return (
    <div className="admin-modal" role="dialog" data-testid="whatsapp-modal">
      <div className="admin-modal-body wide">
        <div className="admin-modal-head">
          <div><span className="eyebrow">WhatsApp reminders</span><h2>{data?.event?.title || "Event"} <small className="wa-when">{when}</small></h2></div>
          <button type="button" onClick={onClose} data-testid="whatsapp-close" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="hint">Tap <b>Send</b> next to a registrant — WhatsApp opens with the reminder pre-filled for their number. Each one is ticked off once you've sent it. {data ? <b data-testid="whatsapp-progress">{sent} of {data.registrants.length} sent</b> : null}</div>
        {err && <div className="error-message" data-testid="whatsapp-error">{err}</div>}
        <label className="wa-filter"><input type="checkbox" checked={onlyPending} onChange={e => setOnlyPending(e.target.checked)} data-testid="whatsapp-only-pending" /> Show only registrants not yet reminded</label>
        <div className="wa-list" data-testid="whatsapp-list">
          {!data ? <div className="hint">Loading registrants…</div> : regs.length ? regs.map(r => {
            const link = waLink(r.phone, eventMessage(r, data.event, data.days_until, settings.short_name || "DCEA"));
            return (
              <div className="list-row" key={r.id} data-testid={`whatsapp-row-${r.id}`}>
                <div><b>{r.name}</b><small>{r.phone || "No phone number"} · {r.email}</small></div>
                {r.whatsapp_sent_at ? <span className="pill paid"><CheckCircle2 size={11} /> Sent {formatDate(r.whatsapp_sent_at)}</span> : <span className="pill">Pending</span>}
                {link ? (
                  <a href={link} target="_blank" rel="noreferrer" className="button button-gold wa-btn" onClick={() => markSent(r)} data-testid={`whatsapp-send-${r.id}`}><MessageCircle size={14} /> Send</a>
                ) : <span className="hint" style={{ padding: "6px 10px" }}>No valid number</span>}
              </div>
            );
          }) : <div className="hint" data-testid="whatsapp-empty">{onlyPending ? "Everyone has been reminded." : "No registrants yet."}</div>}
        </div>
        <div className="admin-modal-actions">
          <button type="button" className="button button-ghost dark" onClick={onClose} data-testid="whatsapp-done">Done</button>
        </div>
      </div>
    </div>
  );
};
